import React, { useEffect, useState } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button, CheckBox, DateBox, NumberBox, SelectBox, TextBox } from 'devextreme-react';
import { formatMessage } from 'devextreme/localization';

import FilterParameterDto from '../../classes/dtos/datasourcemodule/FilterParameterDto';
import FilterParameterValueDto from '../../classes/dtos/datasourcemodule/FilterParameterValueDto';
import FilterParameterVariableDto from '../../classes/dtos/datasourcemodule/FilterParameterVariableDto';
import FilterConstantValue from '../../classes/dtos/datasourcemodule/FilterConstantValue';
import ComparisonOperators from '../../classes/consts/DataSourceModule/ComparisonOperators';
import FieldType from '../../consts/datasourcemodule/FieldType';
import { comparisonOperatorOptionsFromMask } from '../../logics/datasourcemodule/RowFilterLogic';

import './FilterPopup.css';

interface FilterPopupProps {
    visible: boolean;
    title: string;
    currentFilters: FilterParameterDto[];
    // Field the filters apply to (row source field) — carried on the built FilterParameter*Dto.
    fieldReferenceId: number;
    // Type (FieldType) of the ORIGIN column, before transformation — drives the constant editor.
    originFieldType: number;
    // Bit-mask of allowed ComparisonOperators from the source's filtering capabilities (0 => all).
    operators: number;
    // Prefill for a "variable" filter's technical name (already made unique against the other rows).
    prefillTechnicalName: string;
    // Variable technical names already used by the OTHER rows of the grid — must stay unique.
    takenTechnicalNames: string[];
    // Complex ("advanced") filter mode: the popup only NAMES one or more filter variables for the row;
    // operators / grouping / constants are authored in the Filters tab. Simple mode keeps the full
    // constant-or-variable editor below.
    advancedMode: boolean;
    // Complex-mode seed / result: the flat list of declared variable technical names for this row
    // (independent from `currentFilters` / `onApply`, which stay the simple-mode conditions).
    currentDeclaredVariableNames: string[];
    onApplyDeclaredVariableNames(names: string[]): void;
    onApply(filters: FilterParameterDto[]): void;
    onCancel(): void;
}

type FilterKind = 'none' | 'constant' | 'variable';

interface EditBlock {
    kind: FilterKind;
    operator: ComparisonOperators;
    value: string;
}

const emptyBlock = (): EditBlock => ({ kind: 'none', operator: ComparisonOperators.EqualTo, value: '' });

const toBlock = (filter: FilterParameterDto | undefined, originFieldType: number): EditBlock => {
    if (filter instanceof FilterParameterValueDto) {
        // Value = scalaire JSON typé -> chaîne d'édition (ex. "2026-09-09", "42", "true").
        return { kind: 'constant', operator: filter.Operator, value: FilterConstantValue.parse(filter.Value, originFieldType).toEditString() };
    }
    if (filter instanceof FilterParameterVariableDto) {
        return { kind: 'variable', operator: filter.Operator, value: filter.TechnicalName };
    }
    return emptyBlock();
};

// <base>, then <base>_2, _3… until it no longer collides with a taken name.
const uniqueName = (base: string, taken: Iterable<string>): string => {
    const takenLower = new Set(Array.from(taken, (name) => name.trim().toLowerCase()));
    if (!takenLower.has(base.trim().toLowerCase())) {
        return base;
    }
    for (let i = 2; i < 100; i++) {
        const candidate = `${base}_${i}`;
        if (!takenLower.has(candidate.toLowerCase())) {
            return candidate;
        }
    }
    return base;
};

const FilterPopup: React.FC<FilterPopupProps> = ({
    visible, title, currentFilters, fieldReferenceId, originFieldType, operators,
    prefillTechnicalName, takenTechnicalNames, advancedMode, currentDeclaredVariableNames,
    onApplyDeclaredVariableNames, onApply, onCancel,
}) => {

    const [blocks, setBlocks] = useState<EditBlock[]>([emptyBlock(), emptyBlock()]);
    const [secondEnabled, setSecondEnabled] = useState<boolean>(false);

    // Complex mode: the flat list of variable technical names declared for this row.
    const [names, setNames] = useState<string[]>([]);

    useEffect(() => {
        if (!visible) {
            return;
        }
        if (advancedMode) {
            setNames(currentDeclaredVariableNames.length > 0
                ? currentDeclaredVariableNames
                : [uniqueName(prefillTechnicalName, takenTechnicalNames)]);
            return;
        }
        setBlocks([toBlock(currentFilters[0], originFieldType), toBlock(currentFilters[1], originFieldType)]);
        setSecondEnabled(currentFilters.length > 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const patchBlock = (index: number, patch: Partial<EditBlock>): void => {
        setBlocks((previous) => previous.map((block, i) => (i === index ? { ...block, ...patch } : block)));
    };

    // First free "<prefill>", "<prefill>_2", "<prefill>_3"… not already taken by another row or the other block.
    const uniquePrefill = (index: number): string => {
        const taken = new Set(takenTechnicalNames.map((name) => name.trim().toLowerCase()));
        const otherIndex = index === 0 ? 1 : 0;
        if ((otherIndex === 0 || secondEnabled) && blocks[otherIndex].kind === 'variable') {
            taken.add(blocks[otherIndex].value.trim().toLowerCase());
        }
        if (!taken.has(prefillTechnicalName.trim().toLowerCase())) {
            return prefillTechnicalName;
        }
        for (let i = 2; i < 100; i++) {
            const candidate = `${prefillTechnicalName}_${i}`;
            if (!taken.has(candidate.toLowerCase())) {
                return candidate;
            }
        }
        return prefillTechnicalName;
    };

    const changeKind = (index: number, kind: FilterKind): void => {
        setBlocks((previous) => previous.map((block, i) => {
            if (i !== index) {
                return block;
            }
            if (kind === block.kind) {
                return block;
            }
            // Switching TO "variable" from "constant"/"none": drop whatever was typed and prefill a
            // fresh, unique technical name. Switching AWAY from "variable": clear the technical name.
            if (kind === 'variable') {
                return { ...block, kind, value: uniquePrefill(index) };
            }
            return { ...block, kind, value: '' };
        }));
        if (index === 0 && kind === 'none') {
            setSecondEnabled(false);
        }
    };

    const isBlockActive = (index: number): boolean => (index === 0 || secondEnabled) && blocks[index].kind !== 'none';

    const technicalNameError = (index: number): string | null => {
        const block = blocks[index];
        if (!isBlockActive(index) || block.kind !== 'variable') {
            return null;
        }
        const name = block.value.trim();
        if (name.length === 0) {
            return formatMessage('FilterPopup_TechnicalNameRequired');
        }
        const taken = new Set(takenTechnicalNames.map((n) => n.trim().toLowerCase()));
        const otherIndex = index === 0 ? 1 : 0;
        if (isBlockActive(otherIndex) && blocks[otherIndex].kind === 'variable'
            && blocks[otherIndex].value.trim().toLowerCase() === name.toLowerCase()) {
            return formatMessage('FilterPopup_TechnicalNameDuplicate');
        }
        return taken.has(name.toLowerCase()) ? formatMessage('FilterPopup_TechnicalNameDuplicate') : null;
    };

    const hasError = [0, 1].some((index) => technicalNameError(index) !== null);

    // ─── Complex mode: name list ─────────────────────────────────────────────────

    const patchName = (index: number, value: string): void => {
        setNames((previous) => previous.map((name, i) => (i === index ? value : name)));
    };

    const addName = (): void => {
        setNames((previous) => [
            ...previous,
            uniqueName(prefillTechnicalName, [...takenTechnicalNames, ...previous]),
        ]);
    };

    const removeName = (index: number): void => {
        setNames((previous) => previous.filter((_, i) => i !== index));
    };

    const nameError = (index: number): string | null => {
        const name = names[index].trim();
        if (name.length === 0) {
            return null; // blank rows are just dropped on apply
        }
        const taken = new Set(takenTechnicalNames.map((n) => n.trim().toLowerCase()));
        if (taken.has(name.toLowerCase())) {
            return formatMessage('FilterPopup_TechnicalNameDuplicate');
        }
        const isDuplicateHere = names.some((other, i) => i !== index && other.trim().toLowerCase() === name.toLowerCase());
        return isDuplicateHere ? formatMessage('FilterPopup_TechnicalNameDuplicate') : null;
    };

    const namesHaveError = names.some((_, index) => nameError(index) !== null);

    const applyNames = (): void => {
        const seen = new Set<string>();
        const result: string[] = [];
        names.forEach((raw) => {
            const name = raw.trim();
            const key = name.toLowerCase();
            if (name.length === 0 || seen.has(key)) {
                return;
            }
            seen.add(key);
            result.push(name);
        });
        onApplyDeclaredVariableNames(result);
    };

    const apply = (): void => {
        const filters: FilterParameterDto[] = [];
        [0, 1].forEach((index) => {
            if (!isBlockActive(index)) {
                return;
            }
            const block = blocks[index];
            if (block.kind === 'constant') {
                const value = FilterConstantValue.fromInput(block.value, originFieldType).toJson();
                filters.push(new FilterParameterValueDto(0, fieldReferenceId, block.operator, value));
            } else if (block.kind === 'variable') {
                filters.push(new FilterParameterVariableDto(0, fieldReferenceId, block.operator, block.value.trim()));
            }
        });
        onApply(filters);
    };

    const kindOptions = [
        { value: 'none', label: formatMessage('FilterPopup_TypeNone') },
        { value: 'constant', label: formatMessage('FilterPopup_TypeConstant') },
        { value: 'variable', label: formatMessage('FilterPopup_TypeVariable') },
    ];

    const operatorOptions = comparisonOperatorOptionsFromMask(operators);

    const boolOptions = [
        { value: 'true', label: formatMessage('FilterPopup_BoolTrue') },
        { value: 'false', label: formatMessage('FilterPopup_BoolFalse') },
    ];

    const renderConstantEditor = (index: number): JSX.Element => {
        const block = blocks[index];
        const onText = (value: string): void => patchBlock(index, { value });

        switch (originFieldType) {
            case FieldType.Int32:
            case FieldType.BigInt:
                return (
                    <NumberBox
                        value={block.value === '' ? undefined : Number(block.value)}
                        format="#0"
                        stylingMode="outlined"
                        onValueChanged={(e) => onText(e.value == null ? '' : String(e.value))}
                    />
                );
            case FieldType.Decimal:
            case FieldType.Double:
                return (
                    <NumberBox
                        value={block.value === '' ? undefined : Number(block.value)}
                        stylingMode="outlined"
                        onValueChanged={(e) => onText(e.value == null ? '' : String(e.value))}
                    />
                );
            case FieldType.Date:
                return (
                    <DateBox
                        type="date"
                        value={block.value || null}
                        stylingMode="outlined"
                        onValueChanged={(e) => onText(FilterConstantValue.fromInput(e.value, originFieldType).toEditString())}
                    />
                );
            case FieldType.DateTime:
            case FieldType.DateTimeOffset:
                return (
                    <DateBox
                        type="datetime"
                        value={block.value || null}
                        stylingMode="outlined"
                        onValueChanged={(e) => onText(FilterConstantValue.fromInput(e.value, originFieldType).toEditString())}
                    />
                );
            case FieldType.Boolean:
                return (
                    <SelectBox
                        dataSource={boolOptions}
                        displayExpr="label"
                        valueExpr="value"
                        value={block.value === '' ? null : block.value}
                        stylingMode="outlined"
                        onValueChanged={(e) => onText(e.value ?? '')}
                    />
                );
            default:
                return (
                    <TextBox
                        value={block.value}
                        stylingMode="outlined"
                        onValueChanged={(e) => onText(e.value ?? '')}
                    />
                );
        }
    };

    const renderBlock = (index: number): JSX.Element => {
        const block = blocks[index];
        const nameError = technicalNameError(index);

        return (
            <div className="filter-popup-block" key={index}>
                <div className="filter-popup-field">
                    <label>{formatMessage('FilterPopup_Type')}</label>
                    <SelectBox
                        dataSource={kindOptions}
                        displayExpr="label"
                        valueExpr="value"
                        value={block.kind}
                        stylingMode="outlined"
                        onValueChanged={(e) => changeKind(index, (e.value ?? 'none') as FilterKind)}
                    />
                </div>

                {block.kind !== 'none' && (
                    <>
                        <div className="filter-popup-field">
                            <label>{formatMessage('FilterPopup_Operator')}</label>
                            <SelectBox
                                dataSource={operatorOptions}
                                displayExpr="label"
                                valueExpr="value"
                                value={block.operator}
                                stylingMode="outlined"
                                onValueChanged={(e) => patchBlock(index, { operator: e.value })}
                            />
                        </div>
                        <div className="filter-popup-field">
                            <label>{block.kind === 'constant' ? formatMessage('FilterPopup_Value') : formatMessage('FilterPopup_TechnicalName')}</label>
                            {block.kind === 'constant'
                                ? renderConstantEditor(index)
                                : (
                                    <TextBox
                                        value={block.value}
                                        stylingMode="outlined"
                                        isValid={nameError === null}
                                        onValueChanged={(e) => patchBlock(index, { value: e.value ?? '' })}
                                    />
                                )}
                            {nameError && <span className="filter-popup-error">{nameError}</span>}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderNamesContent = (): JSX.Element => (
        <div className="filter-popup-content">
            <div className="filter-popup-field">
                <label>{formatMessage('FilterPopup_TechnicalName')}</label>
                {names.map((name, index) => {
                    const error = nameError(index);
                    return (
                        <div className="filter-popup-name-row" key={index} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <TextBox
                                value={name}
                                stylingMode="outlined"
                                isValid={error === null}
                                width="100%"
                                onValueChanged={(e) => patchName(index, e.value ?? '')}
                            />
                            <Button
                                icon="trash"
                                stylingMode="text"
                                onClick={() => removeName(index)}
                            />
                            {error && <span className="filter-popup-error">{error}</span>}
                        </div>
                    );
                })}
                <Button
                    text={formatMessage('FilterPopup_AddTechnicalName')}
                    icon="add"
                    stylingMode="text"
                    type="default"
                    onClick={addName}
                />
            </div>

            <div className="filter-popup-actions">
                <Button
                    text={formatMessage('FilterPopup_Cancel')}
                    onClick={onCancel}
                    stylingMode="outlined"
                    type="normal"
                />
                <Button
                    text={formatMessage('FilterPopup_Apply')}
                    onClick={applyNames}
                    disabled={namesHaveError}
                    stylingMode="contained"
                    type="default"
                />
            </div>
        </div>
    );

    const renderContent = (): JSX.Element => (
        <div className="filter-popup-content">
            {renderBlock(0)}

            <div className="filter-popup-second-toggle">
                <CheckBox
                    text={formatMessage('FilterPopup_SecondFilter')}
                    value={secondEnabled}
                    disabled={blocks[0].kind === 'none'}
                    onValueChanged={(e) => setSecondEnabled(!!e.value)}
                />
            </div>

            {secondEnabled && blocks[0].kind !== 'none' && renderBlock(1)}

            <div className="filter-popup-actions">
                <Button
                    text={formatMessage('FilterPopup_Cancel')}
                    onClick={onCancel}
                    stylingMode="outlined"
                    type="normal"
                />
                <Button
                    text={formatMessage('FilterPopup_Apply')}
                    onClick={apply}
                    disabled={hasError}
                    stylingMode="contained"
                    type="default"
                />
            </div>
        </div>
    );

    return (
        <Popup
            visible={visible}
            onHiding={onCancel}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title={title}
            width={460}
            height="auto"
            // Build the content (and its DevExtreme editors) at mount rather than on first show,
            // otherwise the initial open stutters while the widgets are instantiated mid-animation.
            deferRendering={false}
            // The default popup "pop" animation (~300ms scale) reads as sluggish here — a short fade.
            animation={{
                show: { type: 'fade', from: 0, to: 1, duration: 90 },
                hide: { type: 'fade', from: 1, to: 0, duration: 60 },
            }}
            contentRender={advancedMode ? renderNamesContent : renderContent}
        />
    );
};

export default FilterPopup;
